import { useState, useEffect } from "react"
import {
  Users,
  Trophy,
  Award,
  Activity,
  ChevronRight,
  Plus,
  Star,
  Calendar,
  Target,
  Zap,
  Flag,
  Check,
  Pencil
} from "lucide-react"
import Navigation from "../components/common/Navigation"
import api from "../request"
import ChallengeModal from "../components/ChallengeModal"
import Header from "../components/common/Header"
import ToastMessage from '../components/ToastMessage';


const ChallengesPage = () => {
  const [activeTab, setActiveTab] = useState("active")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeChallenges, setActiveChallenges] = useState([])
  const [availableChallenges, setAvailableChallenges] = useState([])
  const [completedChallenges, setCompletedChallenges] = useState([])
  const [loading, setLoading] = useState(true)
  const [myChallenges, setmyChallenges] = useState([])
  const [isHotStepper, setIsHotStepper] = useState(false)
  const [goalToEdit, setGoalToEdit] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '' });
  const openEditModal = (goal) => {
    setGoalToEdit(goal); // Set the goal to edit
    setIsModalOpen(true);
  };

  useEffect(() => {
    const checkHotStepperStatus = async () => {
      try {
        const res = await api.get("/user/is-hot-stepper")
        setIsHotStepper(res.data.isHotStepper)
      } catch (err) {
        console.error("Failed to check HotStepper status:", err)
      }
    }

    checkHotStepperStatus()
  }, [])

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const res = await api.get("/challenges")
        const { activeChallenges, availableChallenges, completedChallenges, myChallenges } = res.data

        setActiveChallenges(activeChallenges)
        setAvailableChallenges(availableChallenges)
        setCompletedChallenges(completedChallenges)
        setmyChallenges(myChallenges)
      } catch (error) {
        setToast("Error fetching challenges:", 'error')
      } finally {
        setLoading(false)
      }
    }

    fetchChallenges()
  }, [])

 
  const joinChallenge = async (challengeId) => {
  try {
    const response = await api.post(`/challenges/${challengeId}/join`);
     const challenge = availableChallenges.find((c) => c.id === challengeId)
    if (challenge) {
      // Add to active challenges with 0 progress
      setActiveChallenges([
        ...activeChallenges,
        {
          ...challenge,
          progress: 0,
          status: "just-started",
        },
      ])

      // Remove from available challenges
      setAvailableChallenges(availableChallenges.filter((c) => c.id !== challengeId))
    }
 
    
    // Optional: refetch challenge lists or update UI
    // fetchChallenges(); or manually remove from availableChallenges etc.
  } catch (error) {
    if (error.response && error.response.status === 409) {
      setToast("You have already joined this challenge.", 'error')
    } else {
       setToast("An error occurred while joining the challenge.", 'error')
     
    }
    console.error(error);
  }
};

  // Render active challenges
 const renderActiveChallenges = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {activeChallenges.map((challenge) => {
      const progress = Math.min(challenge.progress, 100); // Cap at 100%
      return (
        <div
          key={challenge.id}
          className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary hover:border-opacity-50"
        >
          {/* Header */}
          <div className="bg-primary bg-opacity-5 p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-xl shadow-sm">{challenge.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                  {challenge.title}
                </h3>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  challenge.status === "on-track"
                    ? "bg-green-100 text-green-800"
                    : challenge.status === "behind"
                      ? "bg-red-100 text-red-800"
                      : "bg-amber-100 text-amber-800"
                }`}
              >
                {challenge.status === "on-track" ? (
                  <div className="flex items-center gap-1">
                    <Target size={12} />
                    <span>On Track</span>
                  </div>
                ) : challenge.status === "behind" ? (
                  <div className="flex items-center gap-1">
                    <Flag size={12} />
                    <span>Falling Behind</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Zap size={12} />
                    <span>Just Started</span>
                  </div>
                )}
              </span>
            </div>
          </div>

          <div className="p-5">
            <p className="text-gray-600 text-sm mb-5 line-clamp-2">{challenge.description}</p>

            {/* Progress Bar */}
            <div className="mb-5">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500 font-medium">Progress</span>
                <span className="font-bold text-primary">{progress}%</span>
              </div>
              <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    challenge.status === "on-track"
                      ? "bg-green-500"
                      : challenge.status === "behind"
                        ? "bg-red-500"
                        : "bg-amber-500"
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Challenge Details */}
            <div className="grid grid-cols-1 gap-3 mb-5">
              <ChallengeDetail
                icon={<Calendar size={12} className="text-gray-400" />}
                label="Duration"
                value={`${challenge.start_date} - ${challenge.end_date}`}
              />
              <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Participants"
                value={challenge.total_participants}
              />
               <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Target"
                value={challenge.target_value}
              />
              <ChallengeDetail
                icon={<Target size={12} className="text-gray-400" />}
                label="Type"
                value={challenge.type}
              />
            </div>

            {/* View Details Button */}
            {/* <button className="w-full bg-primary text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-all group-hover:shadow-md">
              <span>View Details</span>
              <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button> */}
          </div>
        </div>
      );
    })}
  </div>
 );


  // Render available challenges
 const renderAvailableChallenges = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {availableChallenges.map((challenge) => {
      const progress = Math.min(challenge.progress || 0, 100);
      return (
        <div
          key={challenge.id}
          className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary hover:border-opacity-50"
        >
          {/* Header */}
          <div className="bg-blue-50 p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-xl shadow-sm">{challenge.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                {challenge.title}
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{challenge.description}</p>

            {/* Progress Info */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500 font-medium">Progress</span>
                <span className="font-bold text-primary">{progress}%</span>
              </div>
              <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 mb-5">
               
              <ChallengeDetail
                icon={<Calendar size={12} className="text-gray-400" />}
                label="Duration"
                value={`${challenge.start_date} - ${challenge.end_date}`}
              />
              <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Participants"
                value={challenge.participants}
              />
               <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Target"
                value={challenge.target_value}
              />
              <ChallengeDetail
                icon={<Target size={12} className="text-gray-400" />}
                label="Type"
                value={challenge.type}
              />
            </div>

            <button
              className="w-full bg-green-600 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-green-700 transition-all group-hover:shadow-md"
              onClick={() => joinChallenge(challenge.id)}
            >
              <Plus size={16} className="group-hover:rotate-90 transition-transform" />
              <span>Join Challenge</span>
            </button>
          </div>
        </div>
      );
    })}
  </div>
);


  // Render completed challenges
const renderCompletedChallenges = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {completedChallenges.map((challenge) => (
      <div
        key={challenge.id}
        className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary hover:border-opacity-50"
      >
        {/* Header */}
        <div className="bg-green-50 p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-xl shadow-sm">{challenge.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                {challenge.title}
              </h3>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
              <Check size={12} />
              Completed
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <p className="text-gray-600 text-sm mb-5 line-clamp-2">{challenge.description}</p>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <ChallengeDetail
              icon={<Calendar size={12} className="text-gray-400" />}
              label="Duration"
              value={`${challenge.start_date} - ${challenge.end_date}`}
            />
            <ChallengeDetail
              icon={<Users size={12} className="text-gray-400" />}
              label="Participants"
              value={challenge.participants}
            />
            <ChallengeDetail
              icon={<Trophy size={12} className="text-gray-400" />}
              label="Position"
              value={challenge.finalPosition}
            />
          </div>

          <div className="bg-amber-50 p-4 rounded-xl mb-5">
            <div className="flex items-center gap-2">
              <Award size={18} className="text-amber-600" />
              <div>
                <p className="text-xs text-amber-800 font-medium">Reward Earned</p>
                <p className="text-sm font-bold text-amber-900">{challenge.reward}</p>
              </div>
            </div>
          </div>

          <button className="w-full bg-gray-600 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-700 transition-all group-hover:shadow-md">
            <span>View Details</span>
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    ))}
  </div>
);
 const rendermyChallenges = () => ( 
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {myChallenges.map((challenge) => {
      const progress = Math.min(challenge.progress, 100); // Cap at 100%
      return (
        <div
          key={challenge.id}
          className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary hover:border-opacity-50"
        >
          {/* Header */}
          <div className="bg-primary bg-opacity-5 p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-xl shadow-sm">{challenge.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">
                  {challenge.title}
                </h3>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  challenge.status === "on-track"
                    ? "bg-green-100 text-green-800"
                    : challenge.status === "behind"
                      ? "bg-red-100 text-red-800"
                      : "bg-amber-100 text-amber-800"
                }`}
              >
                {challenge.status === "on-track" ? (
                  <div className="flex items-center gap-1">
                    <Target size={12} />
                    <span>On Track</span>
                  </div>
                ) : challenge.status === "behind" ? (
                  <div className="flex items-center gap-1">
                    <Flag size={12} />
                    <span>Falling Behind</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Zap size={12} />
                    <span>Just Started</span>
                  </div>
                )}
              </span>
            </div>
          </div>

          <div className="p-5">
            <p className="text-gray-600 text-sm mb-5 line-clamp-2">{challenge.description}</p>
 

            {/* Challenge Details */}
            <div className="grid grid-cols-1 gap-3 mb-5">
              <ChallengeDetail
                icon={<Calendar size={12} className="text-gray-400" />}
                label="Duration"
                value={`${challenge.start_date} - ${challenge.end_date}`}
              />
              <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Participants"
                value={challenge.total_participants}
              />
               <ChallengeDetail
                icon={<Users size={12} className="text-gray-400" />}
                label="Target"
                value={challenge.target_value}
              />
              <ChallengeDetail
                icon={<Target size={12} className="text-gray-400" />}
                label="Type"
                value={challenge.type}
              />
            </div>

            {/* View Details Button */}
             <button
              onClick={() => openEditModal(challenge)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-700 transition-all group-hover:shadow-md"
            >
              <span>Edit Challenge</span>
              <Pencil size={16} className="group-hover:scale-105 transition-transform" />
            </button>
          </div>
        </div>
      );
    })}
  </div>
);


const ChallengeDetail = ({ icon, label, value }) => (
  <div className="bg-gray-50 p-3 rounded-xl">
    <div className="flex items-center gap-1 mb-1">
      {icon}
      <p className="text-gray-500 text-xs font-medium">{label}</p>
    </div>
    <p className="text-xs font-semibold text-gray-800 line-clamp-1">{value}</p>
  </div>
);

const calculateProgress = (challenge) => {
  if (!challenge.goal || challenge.goal === 0) return 0;
  return Math.min((challenge.current / challenge.goal) * 100, 100);
};


  return (
    <div className="min-h-screen bg-gray-50">
       <ToastMessage
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ message: '', type: '' })}
          />
      {/* Sidebar Navigation */}
      <ChallengeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onGoalSaved={(newChallenge) => {
          // Optionally refresh the list or append to state
          console.log("New challenge saved:", newChallenge)
          setIsModalOpen(false)
        }}

        goalToEdit={goalToEdit}
        // goalCount={/* optional count if needed */}
      />

      <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
        <Navigation />
      </nav>

      {/* Main Content */}
      <div className="bg-gray-50 min-h-screen">
        <Header />
        <main className="max-w-7xl lg:ml-64 overflow-auto">
          <div className="p-6">
            {/* Page Title */}
            <div className="mb-10 text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Fitness Challenges</h1>
              <p className="text-gray-600 text-lg">Push your limits and achieve your fitness goals</p>
            </div>

            {/* Challenge Stats */}
            <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all">
                <div className="flex items-center gap-5">
                  <div className="p-4 bg-primary bg-opacity-10 rounded-xl">
                    <Activity size={28} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Active Challenges</p>
                    <p className="text-3xl font-bold text-gray-900">{activeChallenges.length}</p>
                    <p className="text-xs text-green-600 mt-1">🔥 Keep pushing!</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all">
                <div className="flex items-center gap-5">
                  <div className="p-4 bg-secondary bg-opacity-10 rounded-xl">
                    <Trophy size={28} className="text-secondary" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Completed Challenges</p>
                    <p className="text-3xl font-bold text-gray-900">{completedChallenges.length}</p>
                    <p className="text-xs text-blue-600 mt-1">🏆 Great work!</p>
                  </div>
                </div>
              </div>
              {/* <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all">
                <div className="flex items-center gap-5">
                  <div className="p-4 bg-accent bg-opacity-10 rounded-xl">
                    <Award size={28} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Badges Earned</p>
                    <p className="text-3xl font-bold text-gray-900">5</p>
                    <p className="text-xs text-amber-600 mt-1">⭐ Impressive!</p>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Hot Stepper Feature Promo */}
            {isHotStepper && (
              <div className="bg-primary rounded-2xl shadow-lg p-8 mb-10 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white bg-opacity-10 rounded-full translate-y-24 -translate-x-24"></div>

                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex items-start gap-5">
                    <div className="p-4 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                      <Users size={32} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Hot Stepper Program</h2>
                      <p className="text-blue-100 max-w-md">
                        Create and lead team challenges as an elite performer. Motivate others and earn exclusive
                        rewards!
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all shadow-lg flex items-center gap-2 self-start md:self-center"
                  >
                    <Plus size={18} />
                    <span>Create Challenge</span>
                  </button>
                </div>
              </div>
            )}

            {/* Challenge Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 mb-8">
              <div className="grid grid-cols-4 gap-2">
                <button
                  className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                    activeTab === "active"
                      ? "bg-primary text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setActiveTab("active")}
                >
                  Active
                </button>
                <button
                  className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                    activeTab === "available"
                      ? "bg-primary text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setActiveTab("available")}
                >
                  Available
                </button>

                {/* <button
                  className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                    activeTab === "completed"
                      ? "bg-primary text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setActiveTab("completed")}
                >
                  Completed
                </button> */}
                
                <button
                  className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                    activeTab === "mychallenge"
                      ? "bg-primary text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setActiveTab("mychallenge")}
                >
                  My challenge
                </button>
              </div>
              
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Challenges</h3>
                <p className="text-gray-500">Please wait while we fetch your challenges...</p>
              </div>
            ) : (
              /* Tab Content */
              <div className="space-y-6">
                {activeTab === "active" && renderActiveChallenges()}
                {activeTab === "available" && renderAvailableChallenges()}
                {activeTab === "completed" && renderCompletedChallenges()}
                 {activeTab === "mychallenge" && rendermyChallenges()}
              </div>
            )}
          </div>
        </main>
      </div>

      <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
        <Navigation orientation="horizontal" />
      </div>
    </div>
  )
}

export default ChallengesPage
